/**
 * Node Form Constants
 * Shared constants for NodeUsage, NodeSubUsage, and related form filtering values.
 * These constants mirror the C# NodeFormConstants for consistency across frontend and backend.
 *
 * Maps to: BizFirst.Ai.ProcessEngine.Domain.Node.Services.NodeFormConstants (C#)
 */

/**
 * Resolution tier that determined which form was returned.
 * Matches NodeFormSource on the server.
 */
export type NodeFormSource = 'Settings' | 'NodeType' | 'Common';

/**
 * Node usage slots — these represent the "where" a form is used in the node lifecycle.
 */
export const NODE_USAGE = {
  /** Configuration panel in Flow Studio designer when user clicks "Configure" on a node. */
  DesignerConfigureNode: 'DesignerConfigureNode',

  /** Form shown to user during workflow execution when process pauses waiting for input. */
  RuntimeInputForm: 'RuntimeInputForm',

  /** Read-only review panel in evaluation mode (future use). */
  EvaluationView: 'EvaluationView',
} as const;

export type NodeUsageType = (typeof NODE_USAGE)[keyof typeof NODE_USAGE];

/**
 * Node sub-usage filters — secondary dimension for form filtering within a NodeUsage slot.
 * Maps to Atlas_Forms.NodeSubUsage column.
 */
export const NODE_SUB_USAGE = {
  /** Configuration form shown in the designer's "Configure" panel. */
  NodeDesignConfiguration: 'DesignTime',

  /** Runtime input form for end-user interaction during execution. */
  NodeRuntimeInput: 'RunTime',

  /** Evaluation/review form shown in evaluation mode. */
  NodeEvaluation: 'node-evaluation',

  /** Generic-purpose forms not tied to a specific lifecycle stage. */
  Generic: 'Generic',

  /** Test/sandbox forms used during development. */
  Test: 'Test',

  /** Debug-mode forms for diagnostics. */
  Debug: 'Debug',
} as const;

export type NodeSubUsageType = (typeof NODE_SUB_USAGE)[keyof typeof NODE_SUB_USAGE];

/**
 * Primary usage patterns — maps to Atlas_Forms.PrimaryUsage column.
 * Identifies which tier of the 3-tier resolver registered the form (task007).
 */
export const PRIMARY_USAGE = {
  /** Prefix for node-type-specific forms: "node-form-{NodeTypeCode}" (e.g., "node-form-email-sender"). Mirrors C# NodeFormConstants.PrimaryUsage.NodeTypePrefix. */
  NodeTypePrefix: 'node-form-',

  /** Global forms applicable to all nodes (tier 3 of resolution chain). */
  Common: 'NodeForm-Common',

  /** Constructs PrimaryUsage for a node type — mirrors C# NodeFormConstants.PrimaryUsage.ForNodeType(). */
  forNodeType: (nodeTypeCode: string): string => {
    if (!nodeTypeCode || nodeTypeCode.trim().length === 0) {
      throw new Error('nodeTypeCode cannot be null or empty');
    }
    return `${PRIMARY_USAGE.NodeTypePrefix}${nodeTypeCode}`;
  },
} as const;

/**
 * Resolution tiers — indicates which source provided a form in the 3-tier chain (task007).
 * Maps to the Source field in NodeFormDto and NodeFormResponseDto.
 */
export const RESOLUTION_TIER = {
  /** Tier 1: Form declared explicitly in node configuration settings JSON. */
  Settings: 'Settings',

  /** Tier 2: Form from Atlas_Forms with PrimaryUsage matching the node type. */
  NodeType: 'NodeType',

  /** Tier 3: Form from Atlas_Forms with PrimaryUsage = "NodeForm-Common". */
  Common: 'Common',
} as const;

export type ResolutionTierType = (typeof RESOLUTION_TIER)[keyof typeof RESOLUTION_TIER];

/**
 * Default query parameter values for node-forms API endpoints.
 */
export const FORM_DEFAULTS = {
  /** Default NodeSubUsage path segment. */
  nodeSubUsage: NODE_SUB_USAGE.NodeDesignConfiguration,

  /** Whether to include the full form schema in the response. */
  includeSchema: false,

  /** Whether to compute and return schema checksums. */
  includeChecksum: false,
} as const;
